import { useMutation } from '@apollo/client';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { Chart as ChartJS, registerables } from 'chart.js';
import type { ChartConfiguration } from 'chart.js';
import { assembleChartjs } from 'flint-chart';
import * as React from 'react';
import { useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { chartImageFileName } from './messageContent.ts';
import { withIcon } from '../../core/components/Icon.tsx';
import { UPLOAD_CONVERSATION_ATTACHMENT } from '../queries.ts';

ChartJS.register(...registerables);

const DownloadIcon = withIcon(faDownload);

export const MessageChart = ({
    spec,
    conversationId = null,
    messageId = null,
    chartIndex = 0,
    hasImage = false,
}: {
    spec: object;
    conversationId?: string | null;
    messageId?: string | null;
    chartIndex?: number;
    hasImage?: boolean;
}) => {
    const { t } = useTranslation('chat');
    const chartRef = useRef(null);
    const uploadedRef = useRef(false);
    const [uploadAttachment] = useMutation(UPLOAD_CONVERSATION_ATTACHMENT);
    const config = useMemo(() => {
        try {
            const assembled = assembleChartjs(spec as never) as ChartConfiguration;
            // a degenerate spec can compile without throwing (no chart type, empty datasets, all points
            // dropped) — rendering it would crash react-chartjs-2 or show an empty box
            const points = (assembled?.data?.datasets ?? []).reduce(
                (count, dataset) => count + (dataset.data?.length ?? 0),
                0
            );
            return assembled?.type && points > 0 ? assembled : null;
        } catch {
            // a spec the compiler rejects renders nothing rather than breaking the message
            return null;
        }
    }, [spec]);

    if (!config) return null;

    const storeImage = () => {
        if (!conversationId || !messageId || hasImage || uploadedRef.current) return;
        const canvas = chartRef.current?.canvas;
        if (!canvas) return;
        uploadedRef.current = true;
        canvas.toBlob((blob) => {
            if (!blob) return;
            const file = new File([blob], chartImageFileName(chartIndex), { type: 'image/png' });
            uploadAttachment({
                variables: { conversationId, messageId, file },
                update: (cache, { data }) => {
                    const attachment = data?.uploadAgentConversationAttachment?.attachment;
                    if (!attachment) return;
                    cache.modify({
                        id: cache.identify({ __typename: 'AgentConversationMessageType', id: messageId }),
                        fields: {
                            attachments: (existing = [], { toReference }) => [...existing, toReference(attachment)],
                        },
                    });
                },
            }).catch(() => {});
        }, 'image/png');
    };

    const download = () => {
        const image = chartRef.current?.toBase64Image();
        if (!image) return;
        const anchor = document.createElement('a');
        anchor.href = image;
        anchor.download = 'chart.png';
        anchor.click();
    };

    return (
        <div className="mt-2.5 rounded-xl border border-solid border-line-weak bg-surface-default p-3">
            <div className="relative h-72">
                <Chart
                    ref={chartRef}
                    type={config.type}
                    data={config.data}
                    options={{
                        ...config.options,
                        responsive: true,
                        maintainAspectRatio: false,
                        animation: { ...(config.options?.animation || {}), onComplete: storeImage },
                    }}
                />
            </div>
            <div className="mt-1 flex justify-end">
                <button
                    type="button"
                    onClick={download}
                    className="flex h-7 cursor-pointer items-center gap-1.5 rounded-md border-none bg-transparent px-2 text-xs font-medium text-content-medium transition-colors hover:bg-surface-strong hover:text-content-strong"
                >
                    <DownloadIcon />
                    {t('thread.downloadChart')}
                </button>
            </div>
        </div>
    );
};
